import { useTranslation } from 'react-i18next';

import type { LocalePrefix } from '@tmh-website/shared/const';
import type { NavigationHighlightLinkGroup } from '@tmh-website/shared/cms';
import HorizontalScrollContainer from '@tmh-website/components/shared/HorizontalScrollContainer';
import { isValidLinkField } from '@tmh-website/shared/cms';

import type { HeaderThemeColor } from '../Header.theme';
import NavigationLink from '../NavigationLink';
import NavigationLinkStandalone from '../NavigationLinkStandalone';
import { headerThemes } from '../Header.theme';

import * as Styled from './Navigation.styles';
import { hasGroupChildren } from './Navigation.helpers';

interface NavigationProps {
  locale: LocalePrefix;
  isStandaloneMode: boolean;

  /**
   * Used to prevent flashing when loading a different audience then SSR
   */
  isHidden?: boolean;

  /**
   * The highlight navigation links to display
   */
  visibleHighlightLinkGroups: NavigationHighlightLinkGroup[];

  headerTheme: HeaderThemeColor;
  showSubnav: (id: number) => void;
  hideSubnav: (force?: boolean) => void;
}

const Navigation = ({
  locale,
  isStandaloneMode,
  isHidden,
  visibleHighlightLinkGroups,
  headerTheme,
  showSubnav,
  hideSubnav,
}: NavigationProps) => {
  const { t } = useTranslation();
  if (visibleHighlightLinkGroups.length === 0) {
    return null;
  }

  const Link = isStandaloneMode ? NavigationLinkStandalone : NavigationLink;

  return (
    <Styled.ScrollContainer>
      <HorizontalScrollContainer fadeColor={headerThemes[headerTheme].background}>
        <Styled.Container
          $isHidden={isHidden}
          aria-label={t('global:navigation.highlights.main') as string}
          data-track-highlighted-nav=''>
          {visibleHighlightLinkGroups.map(group => {
            if (isValidLinkField(group.page)) {
              return (
                <Link
                  key={`${group.page.label}-${group.page.value}`}
                  data-cy={!!group.children ? `subnav-link-${group.id}` : undefined}
                  link={group.page}
                  onMouseEnter={() => (hasGroupChildren(group) ? showSubnav(group.id as number) : hideSubnav(true))}
                  onMouseLeave={hasGroupChildren(group) ? () => hideSubnav() : undefined}
                  locale={locale}
                  visibleHighlightLinkGroups={visibleHighlightLinkGroups}
                />
              );
            }
          })}
        </Styled.Container>
      </HorizontalScrollContainer>
    </Styled.ScrollContainer>
  );
};

export default Navigation;
